import React, { useState, useEffect, useCallback } from "react";
import {
  FaTimes,
  FaCrown,
  FaCheckCircle,
  FaSpinner,
  FaRocket,
  FaCoins,
  FaExclamationTriangle,
  FaSync,
  FaUsers,
  FaArrowRight,
  FaCalendarAlt,
  FaHeadset,
} from "react-icons/fa";
import apiRequest from "../services/api";
import DemoModal from "../components/DemoModal";
import { showSuccess } from "../components/dialogHelper";

export default function SubscriptionBilling() {
  const [packages, setPackages] = useState([]);
  const [access, setAccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isActing, setIsActing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(null);

  const [demoOpen, setDemoOpen] = useState(false);

  const [demoForm, setDemoForm] = useState({
    fullName: "",
    businessName: "",
    phone: "",
    type: "",
    email: "",
    message: "",
  });

  const [demoLoading, setDemoLoading] = useState(false);
  const [demoSuccess, setDemoSuccess] = useState("");
  const [demoError, setDemoError] = useState("");

  const WHATSAPP_NUMBER = "251913364827";

  const packageOrder = ["STARTER", "BUSINESS", "GROWTH", "ENTERPRISE"];

  const orderedPackages = [...packages].sort(
    (a, b) =>
      packageOrder.indexOf(String(a.code || "").toUpperCase()) -
      packageOrder.indexOf(String(b.code || "").toUpperCase())
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const [pkgRes, accessRes] = await Promise.all([
        apiRequest("subscriptions/packages"),
        apiRequest("subscriptions/access"),
      ]);

      const pkgList = Array.isArray(pkgRes)
        ? pkgRes
        : Array.isArray(pkgRes?.data)
        ? pkgRes.data
        : [];

      setPackages(pkgList);
      setAccess(accessRes?.data ?? accessRes ?? null);
    } catch (err) {
      setError(err?.message || "Failed to load subscription data.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const formatMoney = (value) =>
    Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString() : "—";

  const handleStartTrial = async () => {
    setIsActing(true);
    setError("");
    setSuccess("");

    try {
      await apiRequest("subscriptions/trial", {
        method: "POST",
      });

      showSuccess("3-day free trial started successfully.");
      setSuccess("Your 3-day free trial has started.");
      await load();
    } catch (err) {
      console.error("Trial error:", err);
      setError(err?.message || "Could not start trial.");
    } finally {
      setIsActing(false);
    }
  };

  const openUpgrade = (pkg) => {
    if (!pkg) return;

    if (String(pkg.pricingType).toUpperCase() === "CUSTOM") {
      setDemoForm((prev) => ({
        ...prev,
        type: `Enterprise subscription - ${pkg.code}`,
        message: `I am interested in the ${pkg.code} plan.`,
      }));
      setDemoOpen(true);
      return;
    }

    setSelectedPkg(pkg);
    setError("");
    setSuccess("");
    setIsModalOpen(true);
  };

  const handleUpgrade = async (e) => {
    e.preventDefault();
    if (!selectedPkg) return;

    setIsActing(true);
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const res = await apiRequest(
        `payments/chapa/checkout/subscription/${selectedPkg.code}`,
        { method: "POST" }
      );

      const checkoutUrl = res?.data?.checkoutUrl || "";

      if (!checkoutUrl) {
        setError(res?.message || "Failed to create Chapa checkout.");
        return;
      }

      window.location.href = checkoutUrl;
    } catch (err) {
      setError(err?.message || "Subscription checkout failed.");
    } finally {
      setIsLoading(false);
      setIsActing(false);
    }
  };

  const handleDemoChange = (e) => {
    const { name, value } = e.target;

    setDemoForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();

    setDemoLoading(true);
    setDemoError("");
    setDemoSuccess("");

    try {
      const response = await apiRequest("subscriptions/leads", {
        method: "POST",
        body: demoForm,
      });

      if (response.success) {
        showSuccess(
          response.message ||
            "Thank you! Your request has been received. We will contact you soon."
        );
      }

      setDemoForm({
        fullName: "",
        businessName: "",
        phone: "",
        type: "",
        email: "",
        message: "",
      });

      setTimeout(() => {
        setDemoOpen(false);
        setDemoSuccess("");
      }, 2500);
    } catch (err) {
      console.error("Lead submission error:", err);

      setDemoError(
        err?.response?.message ||
          err?.message ||
          "Unable to submit your request. Please try again."
      );
    } finally {
      setDemoLoading(false);
    }
  };

  const handleWhatsAppSubmit = async (e) => {
    e.preventDefault();

    if (!demoForm.fullName?.trim() || !demoForm.phone?.trim()) {
      setDemoError("Please fill in the required fields.");
      return;
    }

    setDemoLoading(true);
    setDemoError("");

    try {
      const message = [
        "🔔 *New Enterprise Request – MUYAZ BUSINESS OS*",
        "",
        `*Name:* ${demoForm.fullName.trim()}`,
        `*Business:* ${demoForm.businessName?.trim() || ""}`,
        `*Phone:* ${demoForm.phone.trim()}`,
        `*Email:* ${demoForm.email || ""}`,
        `*Type:* ${demoForm.type || ""}`,
        `*Message:* ${demoForm.message || ""}`,
        "",
        "_Submitted from Muyaz Business OS_",
      ]
        .filter(Boolean)
        .join("\n");

      const encoded = encodeURIComponent(message);
      const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`;

      window.open(waUrl, "_blank", "noopener,noreferrer");

      setDemoSuccess(true);
    } catch (err) {
      setDemoError(
        "Something went wrong. Please try again or message us on WhatsApp."
      );
    } finally {
      setDemoLoading(false);
    }
  };

  const getStatusClass = () => {
    if (access?.status === "ACTIVE") {
      return "text-emerald-700 border-emerald-200 bg-emerald-50 dark:text-emerald-400 dark:border-emerald-500/20 dark:bg-emerald-500/10";
    }

    if (
      access?.status === "EXPIRED" ||
      access?.requiresUpgrade ||
      access?.status === "BLOCKED"
    ) {
      return "text-amber-700 border-amber-200 bg-amber-50 dark:text-amber-400 dark:border-amber-500/20 dark:bg-amber-500/10";
    }

    return "text-slate-500 border-slate-200 bg-slate-50 dark:text-slate-400 dark:border-white/10 dark:bg-slate-950/50";
  };

  const getPackageFeatures = (pkg) => {
    const code = String(pkg.code || "").toUpperCase();

    if (code === "STARTER") {
      return [
        "Core ERP modules",
        "Sales & inventory",
        "Accounting",
        "Business reports",
        "Standard support",
      ];
    }

    if (code === "BUSINESS") {
      return [
        "Everything in Starter",
        "More team members",
        "Higher AI allowance",
        "Priority support",
        "Growing business tools",
      ];
    }

    if (code === "GROWTH") {
      return [
        "Everything in Business",
        "Larger team capacity",
        "Advanced business tools",
        "Higher AI allowance",
        "Priority support",
      ];
    }

    return [
      "Everything in Growth",
      "Custom business configuration",
      "Dedicated support",
      "Custom integrations",
      "Advanced enterprise requirements",
      "Custom development",
    ];
  };

  const getPackageStyle = (pkg) => {
    const code = String(pkg.code || "").toUpperCase();

    if (code === "BUSINESS") {
      return "border-emerald-200 bg-emerald-50/60 shadow-sm dark:border-emerald-500/40 dark:bg-emerald-500/5 dark:shadow-2xl dark:shadow-emerald-500/5";
    }

    if (code === "GROWTH") {
      return "border-sky-200 bg-sky-50/50 dark:border-sky-500/30 dark:bg-slate-900/70";
    }

    if (code === "ENTERPRISE") {
      return "border-violet-200 bg-violet-50/50 dark:border-violet-500/30 dark:bg-violet-500/5";
    }

    return "border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900/60 dark:shadow-none";
  };

  const getButtonStyle = (pkg) => {
    const code = String(pkg.code || "").toUpperCase();

    if (code === "BUSINESS") {
      return "bg-emerald-600 hover:bg-emerald-500 text-white";
    }

    if (code === "GROWTH") {
      return "bg-sky-600 hover:bg-sky-500 text-white";
    }

    if (code === "ENTERPRISE") {
      return "bg-violet-600 hover:bg-violet-500 text-white";
    }

    return "border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-800 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10 dark:text-white";
  };

  const cardClass =
    "bg-white border border-slate-200 shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl";

  const innerCardClass =
    "bg-slate-50 border-slate-200 dark:bg-slate-950/50 dark:border-white/10";

  return (
    <>
      <div className="min-h-full p-4 pb-24 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100 md:p-8">
        <div className="mx-auto max-w-7xl space-y-6">
          {/* HEADER */}
          <div className="flex flex-col gap-4 p-6 border rounded-2xl backdrop-blur-md bg-white border-slate-200 shadow-sm dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="flex items-center gap-2 text-xl font-bold tracking-tight text-slate-900 dark:text-white">
                <FaCrown className="text-amber-500 dark:text-amber-400" />
                Subscription & Billing
              </h1>

              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Manage your MBOS subscription for your organization. AI usage is
                deducted from your plan balance.
              </p>
            </div>

            <button
              type="button"
              onClick={load}
              disabled={isLoading}
              className="flex items-center gap-2 px-3 py-2 text-xs font-medium transition border rounded-xl bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-50 dark:bg-slate-950/60 dark:border-white/10 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              <FaSync className={isLoading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>

          {/* ERROR */}
          {error && (
            <div className="flex items-start gap-2 p-4 text-xs font-medium border rounded-xl bg-red-50 border-red-200 text-red-700 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
              <FaExclamationTriangle className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* SUCCESS */}
          {success && (
            <div className="flex items-center gap-2 p-4 text-xs font-medium border rounded-xl bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-500/10 dark:border-emerald-500/20 dark:text-emerald-400">
              <FaCheckCircle />
              {success}
            </div>
          )}

          {/* CURRENT SUBSCRIPTION */}
          <div className={`p-6 space-y-5 rounded-2xl ${cardClass}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-bold tracking-wider uppercase text-slate-900 dark:text-white">
                  Current subscription
                </h2>
              </div>

              {access && (
                <span
                  className={`px-3 py-1.5 rounded-full text-[10px] font-bold border ${getStatusClass()}`}
                >
                  {access.status || "UNKNOWN"}
                  {access.type ? ` · ${access.type}` : ""}
                </span>
              )}
            </div>

            {isLoading && !access ? (
              <div className="text-xs text-slate-500 dark:text-slate-500">
                Loading subscription…
              </div>
            ) : access ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {/* STATUS */}
                <div className={`p-4 border rounded-xl ${innerCardClass}`}>
                  <p className="mb-1 text-[10px] font-bold uppercase text-slate-500 dark:text-slate-500">
                    Status
                  </p>

                  <p className="text-sm font-bold text-slate-900 dark:text-white">
                    {access.status || "—"}
                  </p>

                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-500">
                    {access.type || "No subscription"}
                  </p>
                </div>

                {/* PERIOD */}
                <div className={`p-4 border rounded-xl ${innerCardClass}`}>
                  <p className="flex items-center gap-1 mb-1 text-[10px] font-bold uppercase text-slate-500 dark:text-slate-500">
                    <FaCalendarAlt />
                    Period ends
                  </p>

                  <p className="font-mono text-sm text-slate-800 dark:text-white">
                    {formatDate(access.endAt)}
                  </p>
                </div>

                {/* AI BALANCE */}
                <div className={`p-4 border rounded-xl ${innerCardClass}`}>
                  <p className="flex items-center gap-1 mb-1 text-[10px] font-bold uppercase text-slate-500 dark:text-slate-500">
                    <FaCoins className="text-amber-500 dark:text-amber-400" />
                    AI Service
                  </p>

                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-500">
                    {formatMoney(access.aiRatePerCall)} ETB / call
                  </p>
                </div>

                {/* ACCESS */}
                <div className={`p-4 border rounded-xl ${innerCardClass}`}>
                  <p className="mb-1 text-[10px] font-bold uppercase text-slate-500 dark:text-slate-500">
                    Application access
                  </p>

                  <p
                    className={`text-sm font-bold ${
                      access.appAllowed
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-amber-600 dark:text-amber-400"
                    }`}
                  >
                    {access.appAllowed ? "Allowed" : "Restricted"}
                  </p>

                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-500">
                    {access.message || "—"}
                  </p>
                </div>
              </div>
            ) : null}

            {/* TRIAL */}
            {access?.status === "NONE" && (
              <div className="flex flex-col gap-4 p-5 border sm:flex-row sm:items-center sm:justify-between rounded-xl border-emerald-200 bg-emerald-50 dark:border-emerald-500/20 dark:bg-emerald-500/5">
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                    Start your free trial
                  </h3>

                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Try MBOS free for 3 days before choosing a subscription.
                  </p>
                </div>

                <button
                  type="button"
                  disabled={isActing}
                  onClick={handleStartTrial}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 text-xs font-semibold text-white transition rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60"
                >
                  {isActing ? (
                    <FaSpinner className="animate-spin" />
                  ) : (
                    <FaRocket />
                  )}
                  Start 3-day free trial
                </button>
              </div>
            )}

            {access?.requiresUpgrade && (
              <div className="p-4 text-xs border rounded-xl text-amber-700 border-amber-200 bg-amber-50 dark:text-amber-400 dark:border-amber-500/20 dark:bg-amber-500/5">
                Your subscription requires an upgrade to continue using the
                application.
              </div>
            )}
          </div>

          {/* PACKAGES */}
          <div>
            <div className="mb-6">
              <h2 className="text-sm font-bold tracking-wider uppercase text-slate-900 dark:text-white">
                Choose your plan
              </h2>

              <p className="mt-1 text-xs text-slate-500 dark:text-slate-500">
                Simple monthly pricing that grows with your business.
              </p>
            </div>

            {isLoading && packages.length === 0 ? (
              <div className="text-xs text-slate-500">Loading packages…</div>
            ) : packages.length === 0 ? (
              <div className="p-8 text-xs text-center border rounded-2xl text-slate-500 bg-white border-slate-200 shadow-sm dark:bg-slate-900/50 dark:border-white/10 dark:shadow-none">
                No subscription packages configured.
              </div>
            ) : (
              <div className="grid gap-6 lg:grid-cols-4">
                {orderedPackages.map((pkg) => {
                  const code = String(pkg.code || "").toUpperCase();
                  const pricingType = String(
                    pkg.pricingType || ""
                  ).toUpperCase();

                  const isBusiness = code === "BUSINESS";
                  const isGrowth = code === "GROWTH";
                  const isEnterprise = pricingType === "CUSTOM";

                  const features = getPackageFeatures(pkg);

                  return (
                    <div
                      key={pkg.id || pkg.code}
                      className={`relative flex flex-col p-6 transition border rounded-3xl ${getPackageStyle(
                        pkg
                      )}`}
                    >
                      {/* BADGES */}
                      {isBusiness && (
                        <div className="absolute right-5 top-0 px-3 py-1 text-[9px] font-black tracking-wider -translate-y-1/2 rounded-full bg-emerald-500 text-slate-950">
                          MOST POPULAR
                        </div>
                      )}

                      {isGrowth && (
                        <div className="absolute right-5 top-0 px-3 py-1 text-[9px] font-black tracking-wider -translate-y-1/2 rounded-full bg-sky-400 text-slate-950">
                          ADVANCED
                        </div>
                      )}

                      {isEnterprise && (
                        <div className="absolute right-5 top-0 px-3 py-1 text-[9px] font-black tracking-wider -translate-y-1/2 rounded-full bg-violet-400 text-slate-950">
                          ENTERPRISE
                        </div>
                      )}

                      {/* PLAN NAME */}
                      <div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                          {code}
                        </h3>

                        <p className="min-h-[42px] mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                          {pkg.name}
                        </p>
                      </div>

                      {/* PRICE */}
                      {!isEnterprise && (
                        <div className="mt-7">
                          <div className="flex items-end gap-2">
                            <span className="text-4xl font-black text-slate-900 dark:text-white">
                              {formatMoney(pkg.price)}
                            </span>

                            <span className="mb-1 text-xs text-slate-500">
                              ETB
                            </span>
                          </div>

                          <p className="mt-1 text-xs text-slate-500">/ month</p>
                        </div>
                      )}

                      {/* USER CAPACITY */}
                      {!isEnterprise && (
                        <div className="grid grid-cols-2 gap-2 mt-6">
                          <div
                            className={`p-3 border rounded-xl ${innerCardClass}`}
                          >
                            <FaUsers className="mb-2 text-emerald-600 dark:text-emerald-400" />

                            <p className="text-[10px] uppercase font-bold text-slate-500">
                              Users
                            </p>

                            <p className="mt-1 text-sm font-bold text-slate-900 dark:text-white">
                              {pkg.includedUsers || 0}
                            </p>
                          </div>

                          <div
                            className={`p-3 border rounded-xl ${innerCardClass}`}
                          >
                            <FaArrowRight className="mb-2 text-amber-500 dark:text-amber-400" />

                            <p className="text-[10px] uppercase font-bold text-slate-500">
                              Extra user
                            </p>

                            <p className="mt-1 text-sm font-bold text-slate-900 dark:text-white">
                              {formatMoney(pkg.additionalUserPrice)} ETB
                            </p>
                          </div>
                        </div>
                      )}

                      {/* AI */}
                      {!isEnterprise && (
                        <div
                          className={`p-4 mt-3 border rounded-xl ${innerCardClass}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] text-slate-500">
                              AI cost / call
                            </span>

                            <span className="font-mono text-[10px] text-slate-600 dark:text-slate-300">
                              {formatMoney(pkg.aiRatePerCall)} ETB
                            </span>
                          </div>
                        </div>
                      )}

                      {/* FEATURES */}
                      <ul className="flex-1 mt-7 space-y-3">
                        {features.map((feature) => (
                          <li
                            key={feature}
                            className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-300"
                          >
                            <FaCheckCircle className="mt-0.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {/* BUTTON */}
                      <button
                        type="button"
                        disabled={isActing}
                        onClick={() => openUpgrade(pkg)}
                        className={`flex items-center justify-center gap-2 w-full mt-8 py-3 rounded-xl text-xs font-bold transition disabled:opacity-50 ${getButtonStyle(
                          pkg
                        )}`}
                      >
                        {isEnterprise ? (
                          <>
                            <FaHeadset />
                            Talk to Us
                          </>
                        ) : (
                          <>
                            <FaRocket />
                            Choose {code}
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* PAYMENT MODAL */}
          <div
            className={`fixed inset-0 z-[2000] flex flex-col justify-end transition-opacity duration-300 ${
              isModalOpen
                ? "opacity-100 pointer-events-auto"
                : "opacity-0 pointer-events-none"
            }`}
          >
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm dark:bg-slate-950/70" />

            <div
              className={`relative w-full max-w-lg mx-auto p-6 sm:p-8 space-y-6 border rounded-t-3xl shadow-2xl transition-transform duration-300 bg-white border-slate-200 dark:bg-slate-900 dark:border-white/10 ${
                isModalOpen ? "translate-y-0" : "translate-y-full"
              }`}
            >
              {/* MODAL HEADER */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900 dark:text-white">
                    <FaCrown className="text-amber-500 dark:text-amber-400" />
                    Confirm subscription
                  </h3>
                </div>

                <button
                  type="button"
                  disabled={isActing}
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 transition border rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-600 border-slate-200 dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/10"
                >
                  <FaTimes />
                </button>
              </div>

              {/* PACKAGE SUMMARY */}
              {selectedPkg && (
                <div
                  className={`p-5 space-y-4 border rounded-2xl ${innerCardClass}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      Subscription
                    </span>

                    <span className="text-sm font-bold text-slate-900 dark:text-white">
                      {selectedPkg.code}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      Monthly price
                    </span>

                    <span className="font-mono text-lg font-bold text-emerald-600 dark:text-emerald-400">
                      {formatMoney(selectedPkg.price)} ETB
                    </span>
                  </div>
                </div>
              )}

              {/* PAYMENT FORM */}
              <form onSubmit={handleUpgrade} className="space-y-5">
                <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/10">
                  <button
                    type="button"
                    disabled={isActing}
                    onClick={() => setIsModalOpen(false)}
                    className="px-5 py-2.5 text-xs font-semibold transition rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={isActing || !selectedPkg}
                    className="flex items-center gap-2 px-6 py-2.5 text-xs font-bold text-white transition rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60"
                  >
                    {isActing ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Processing…
                      </>
                    ) : (
                      <>
                        <FaCheckCircle />
                        Confirm & Activate
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* ENTERPRISE DEMO / CONTACT */}
      <DemoModal
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        form={demoForm}
        onChange={handleDemoChange}
        onSubmit={handleDemoSubmit}
        onWhatsApp={handleWhatsAppSubmit}
        loading={demoLoading}
        error={demoError}
        success={demoSuccess}
      />
    </>
  );
}
